import type { SlopeDef } from './types'

/**
 * The slope roster.
 *
 * Each of these is a course design, not a seed. The job of a racing slope is to
 * offer more than one line that could plausibly be fastest, so that learning
 * the mountain is what makes you quicker — the route-reading the spec cares
 * about (§1 pillar 2) survives the move to a clock intact, it just gets
 * measured in seconds instead of judged.
 *
 * ## Corridors
 *
 * Every slope is now built around a **corridor**: a natural drainage the run
 * follows, 26-80m across, walled on both sides and meandering the whole way
 * down. This replaced open 470-780m faces, which had a problem no amount of
 * feature placement fixed — on an open face the fastest line is *nearly the
 * fall line, always*, so route-reading collapsed into "point it downhill and
 * hope". You could also simply lose the run.
 *
 * A corridor fixes both without adding a single gate or marker. The ground
 * leads you, so the course is legible on sight; and because it bends, the fall
 * line and the racing line stop being the same thing. What's left to decide is
 * genuinely open:
 *
 *   - **high or low through a bend.** The banking lifts the outside, so a high
 *     line carries speed round; the inside is shorter, flatter and scrubbier.
 *   - **which side of an island**, where a rib splits the floor.
 *   - **the ramp or the step**, at every cliff band.
 *   - **how straight you dare run a choke**, where the floor pinches to 26m.
 *
 * The levers that make a line fast, and therefore the levers a design pulls:
 *
 *   - **snow**: powder ploughs (resistance 0.205), wind slab runs (0.034). A
 *     fast line hunts firm snow, which is why conditions are authored per slope.
 *   - **directness**: the shortest path through a bend is rarely the fastest.
 *   - **cliffs**: air has no friction. Dropping a band is a genuine shortcut if
 *     you can land it on steep ground and carry the speed out.
 *   - **traps**: gully bottoms and flat runouts bleed speed you cannot get back.
 *   - **sluff**: hesitate on steep loose snow and it catches you — and a walled
 *     corridor gives it nowhere to go but down the line you are on.
 */

const NORTH_FACE: SlopeDef = {
  id: 'north-face',
  name: 'North Chute',
  blurb: 'Five bends, one choke, and a step you can jump or ride around.',
  difficulty: 'blue',
  version: 10,

  width: 185,
  length: 540,
  vertical: 380,

  // Bench at 0.42 to set up the cliff band, blind rollover at 0.72 into the
  // steepest pitch on the slope, then a long fast apron to punish anyone who
  // arrives slow.
  profile: [
    [0, 19], [0.05, 38], [0.24, 41], [0.42, 35],
    [0.50, 39], [0.62, 43], [0.72, 32], [0.80, 45],
    [0.88, 28], [0.95, 18], [1.0, 12],
  ],

  // Five bends. The tightest, at 0.50, is also where the cliff band crosses and
  // where the floor pinches to 32m — one place on the run where every decision
  // has to be made at once.
  corridor: {
    path: [[0, 0], [0.15, -21], [0.33, 21], [0.51, -21], [0.69, 20], [0.86, -10], [1.0, 0]],
    width: [[0, 24], [0.20, 19], [0.36, 19], [0.50, 18], [0.62, 22], [0.78, 17], [0.90, 24], [1.0, 34]],
    wallHeight: 33,
    wallRun: 35,
    banking: 1.20,
    fluting: 2.0,
  },

  couloirs: [
    // A trench on the *inside* of the fourth bend: sheltered, so it holds soft
    // snow. Shortest way round and the slowest surface, against a fast bank
    // directly above it.
    { xTop: 6, xBottom: 6, from: 0.54, to: 0.74, halfWidth: 6, depth: 4.5 },
  ],
  spines: [
    // A rib splitting the floor through the second bend. Wind-exposed crest, so
    // it is the fastest surface here — but you commit to a side early.
    // Amplitude held to ~0.2x the rib wavelength (2*span/ribs = 16m), per the
    // rule in shapes.ts: past that a rib stops being a crest you ride and
    // becomes a fin you fall off, and across a floor this narrow there is no
    // way around it. At 6m it cost the par line sixteen seconds.
    { centreX: 0, span: 16, ribs: 2, amplitude: 3.2, from: 0.20, to: 0.32 },
  ],
  cliffBands: [
    // In the choke. The easy ramp is on the inside — the low, short, slow line
    // — so taking the fast bank means taking the 11m step off it.
    { at: 0.50, drop: 9, gates: [
      { x: -7, halfWidth: 6, ease: 1.0 },
      { x: 8, halfWidth: 5, ease: 0.5 },
    ] },
  ],
  rollovers: [
    { at: 0.72, x: 0, width: 30, height: 5 },
  ],
  drainages: [
    { x: 0, from: 0.86, to: 0.95, width: 20, depth: 5 },
  ],

  // The corridor walls do the containing now, so the rim only has to close off
  // the ground beyond the shoulders.
  naturalJumps: 12,

  rim: 0.28,
  grain: [2.2, 1.2, 0.2],
  erosionDroplets: 26000,
  repose: 47,

  treeline: 0.42,
  treeDensity: 1.0,

  // North-facing and cold: the sheltered trench keeps powder, the exposed rib
  // and the banks get scoured firm. That split is the whole route puzzle.
  conditions: {
    windDir: 118, windStrength: 0.55, tempC: -11,
    daysSinceSnow: 2.5, newSnowCm: 30, sun: 0.35, sunAspect: 30,
    summary: '30cm two days ago · moderate cross-loading · -11°C',
  },

  start: { x: 0, z: 20 },
  checkpoints: [0.33, 0.66],
  finish: 0.97,
}

const THE_GULLET: SlopeDef = {
  id: 'the-gullet',
  name: 'The Gullet',
  blurb: 'Seven bends, two rock bands, and a 26m choke at speed.',
  difficulty: 'black',
  version: 10,

  width: 160,
  length: 565,
  vertical: 470,

  // Shaved ~3° off the old open-face profile. A 43° sustained pitch was fine
  // when the face was 470m wide and you could pick a mellow line down it; in a
  // 40m corridor there is nowhere to go, and it measured 51% of the racing
  // floor above 45° with 63% no-fall. Still comfortably the steepest of the
  // three, and still the only one that will hurt you.
  profile: [
    [0, 21], [0.05, 41], [0.22, 44], [0.40, 40],
    [0.48, 43], [0.66, 45], [0.76, 36], [0.84, 44],
    // Kept steeper through the runout than it looks like it needs: the central
    // drainage below 0.72 is deep, and two release points came to rest in it
    // when the tail mellowed to 10°.
    [0.91, 29], [0.96, 21], [1.0, 15],
  ],

  // Tight, fast and relentless: a bend every ~130m of descent, so there is no
  // straight long enough to relax on. The 0.58 choke pinches to 40m across at
  // the steepest point of the run.
  corridor: {
    path: [
      [0, 0], [0.12, 18], [0.27, -18], [0.41, 18],
      [0.55, -18], [0.69, 17], [0.84, -10], [1.0, 0],
    ],
    width: [
      [0, 22], [0.15, 17], [0.30, 15], [0.44, 16],
      [0.58, 13], [0.72, 15], [0.86, 22], [1.0, 32],
    ],
    wallHeight: 32,
    wallRun: 30,
    banking: 1.35,
    fluting: 2.4,
  },

  couloirs: [
    // Grooves worn into one side of the floor, each on the inside of a bend.
    // Shortest way through and the slowest surface on the slope.
    { xTop: -5, xBottom: 5, from: 0.14, to: 0.30, halfWidth: 5, depth: 5 },
    { xTop: 5, xBottom: -5, from: 0.46, to: 0.62, halfWidth: 5, depth: 5 },
  ],
  spines: [],
  cliffBands: [
    { at: 0.34, drop: 11, gates: [
      { x: -7, halfWidth: 4, ease: 0.95 },
      { x: 7, halfWidth: 3, ease: 0.5 },
    ] },
    // Now that the pitch here is 48°, 13m of air with one way round it was
    // both unfair and self-defeating: the face stood at 71° composite, shed
    // under thermal transport, and piled a flat bench at its own foot that two
    // zero-input releases came to rest on. 10m with a second weakness.
    { at: 0.62, drop: 10, gates: [
      { x: -6, halfWidth: 4, ease: 0.9 },
      { x: 7, halfWidth: 3, ease: 0.45 },
    ] },
  ],
  rollovers: [
    { at: 0.76, x: 0, width: 22, height: 6 },
  ],
  drainages: [
    { x: 0, from: 0.84, to: 0.94, width: 16, depth: 5 },
  ],

  naturalJumps: 14,

  rim: 0.28,
  grain: [2.0, 1.1, 0.18],
  erosionDroplets: 34000,
  repose: 50,

  treeline: 0.22,
  treeDensity: 0.45,

  // Wind-hammered and old: firm, fast and unforgiving. Ice on the banks.
  conditions: {
    windDir: 85, windStrength: 0.85, tempC: -16,
    daysSinceSnow: 8, newSnowCm: 12, sun: 0.5, sunAspect: 10,
    summary: '8 days since snow · stripped by wind · -16°C',
  },

  start: { x: 0, z: 22 },
  checkpoints: [0.30, 0.58, 0.80],
  finish: 0.97,
}

const LARCH_BOWL: SlopeDef = {
  id: 'larch-bowl',
  name: 'Larch Gully',
  blurb: 'A treed drainage with four lazy bends. Where you learn to read a line.',
  difficulty: 'green',
  version: 10,

  width: 195,
  length: 490,
  vertical: 280,

  profile: [
    [0, 16], [0.06, 29], [0.28, 33], [0.46, 29],
    [0.58, 34], [0.70, 30], [0.82, 25], [0.92, 17], [1.0, 11],
  ],

  // Four long, lazy bends with the widest floor on the roster — 42m at its
  // tightest. A beginner should be able to see the whole of the next turn from
  // the top of the one they are in.
  corridor: {
    path: [[0, 0], [0.20, 20], [0.42, -20], [0.64, 20], [0.84, -12], [1.0, 0]],
    width: [[0, 27], [0.22, 23], [0.46, 21], [0.66, 24], [0.85, 31], [1.0, 40]],
    wallHeight: 27,
    wallRun: 36,
    banking: 0.95,
    fluting: 1.4,
  },

  couloirs: [],
  spines: [
    // A low island in the middle of the floor. The first real decision on the
    // slope, and it is a free one: both sides work.
    { centreX: 0, span: 13, ribs: 1, amplitude: 4.5, from: 0.26, to: 0.40 },
  ],
  // One low step, gated generously — a beginner slope should never present an
  // unavoidable drop (spec §12 risk #5: this is what stops day-two churn).
  cliffBands: [
    { at: 0.54, drop: 6, gates: [
      { x: -12, halfWidth: 8, ease: 1.0 },
      { x: 11, halfWidth: 8, ease: 0.95 },
    ] },
  ],
  rollovers: [
    { at: 0.66, x: 7, width: 26, height: 5 },
  ],
  drainages: [
    { x: -2, from: 0.70, to: 0.88, width: 20, depth: 4 },
  ],

  naturalJumps: 9,

  rim: 0.25,
  grain: [1.8, 1.0, 0.16],
  erosionDroplets: 20000,
  repose: 43,

  treeline: 0.62,
  treeDensity: 1.35,

  // Deep, cold and sheltered. Slow snow everywhere, so the fast line is about
  // staying off the flats rather than finding firm patches.
  conditions: {
    windDir: 150, windStrength: 0.2, tempC: -7,
    daysSinceSnow: 0.5, newSnowCm: 45, sun: 0.25, sunAspect: 40,
    summary: '45cm overnight · light wind · -7°C',
  },

  start: { x: 0, z: 18 },
  checkpoints: [0.35, 0.70],
  finish: 0.96,
}

export const SLOPES: readonly SlopeDef[] = [LARCH_BOWL, NORTH_FACE, THE_GULLET]

export const DEFAULT_SLOPE_ID = 'north-face'

export function slopeById(id: string): SlopeDef {
  return SLOPES.find((s) => s.id === id) ?? SLOPES.find((s) => s.id === DEFAULT_SLOPE_ID)!
}
